/**
 * Theme Config Factory
 * Creates MUI theme from base tokens + client overrides
 */

import { createTheme, ThemeOptions, Theme } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';
import { contentSpacing } from '../../tokens/base/contentStyles';
import {
  baseSpacing,
  baseBreakpoints,
  baseTypography,
  baseShadows,
  baseBorderRadius,
  baseTransitions,
  baseFocus,
  semanticColors,
  semanticColorsDark,
} from '../../tokens';
import type { ClientThemeOverrides } from './themeAugmentation';

/**
 * Creates a complete MUI theme configuration
 * @param clientOverrides - Client-specific theme customizations
 * @param mode - 'light' or 'dark' mode
 * @returns Complete MUI Theme object
 */
export function createThemeConfig(
  clientOverrides: ClientThemeOverrides,
  mode: 'light' | 'dark' = 'light'
): Theme {
  // Base theme configuration using design tokens
  const baseTheme: ThemeOptions = {
    spacing: baseSpacing.unit,
    breakpoints: baseBreakpoints,
    typography: {
      fontFamily: baseTypography.fontFamily.primary,
      ...baseTypography.variants,
    },
    palette: {
      mode,
      ...(mode === 'dark' ? semanticColorsDark : semanticColors),
    },
    // Fluent 2 soft shadows
    shadows: [
      baseShadows.none,
      baseShadows.elevation[1],
      baseShadows.elevation[2],
      baseShadows.elevation[2],   // 3 uses 2
      baseShadows.elevation[4],
      baseShadows.elevation[4],   // 5 uses 4
      baseShadows.elevation[4],   // 6 uses 4
      baseShadows.elevation[4],   // 7 uses 4
      baseShadows.elevation[8],
      baseShadows.elevation[8],   // 9 uses 8
      baseShadows.elevation[8],   // 10 uses 8
      baseShadows.elevation[8],   // 11 uses 8
      baseShadows.elevation[8],   // 12 uses 8
      baseShadows.elevation[8],   // 13 uses 8
      baseShadows.elevation[8],   // 14 uses 8
      baseShadows.elevation[8],   // 15 uses 8
      baseShadows.elevation[16],
      baseShadows.elevation[16],  // 17 uses 16
      baseShadows.elevation[16],  // 18 uses 16
      baseShadows.elevation[16],  // 19 uses 16
      baseShadows.elevation[16],  // 20 uses 16
      baseShadows.elevation[16],  // 21 uses 16
      baseShadows.elevation[16],  // 22 uses 16
      baseShadows.elevation[16],  // 23 uses 16
      baseShadows.elevation[24],
    ],
    shape: {
      borderRadius: baseBorderRadius.sm,  // 6px global default (Atlas)
    },
    transitions: {
      duration: {
        shortest: baseTransitions.duration.fastest,
        shorter: baseTransitions.duration.fast,
        short: baseTransitions.duration.normal,
        standard: baseTransitions.duration.normal,
        complex: baseTransitions.duration.slow,
        enteringScreen: baseTransitions.duration.fast,
        leavingScreen: baseTransitions.duration.fast,
      },
      easing: baseTransitions.easing,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            fontFamily: baseTypography.fontFamily.primary,
            fontStyle: 'normal',
          },
          'em, i': {
            fontStyle: 'normal',
          },
          'code, kbd, pre, samp': {
            fontFamily: baseTypography.fontFamily.primary,
          },
        },
      },
      // Base component overrides - Atlas Design System
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: baseBorderRadius.lg,  // 12px for cards (Atlas)
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: baseSpacing.unit * baseSpacing.patterns.cardPadding,
            '&:last-child': {
              paddingBottom: baseSpacing.unit * baseSpacing.patterns.cardPadding,
            },
          },
        },
      },
      MuiContainer: {
        styleOverrides: {
          root: {
            paddingLeft: baseSpacing.unit * baseSpacing.patterns.containerPadding,
            paddingRight: baseSpacing.unit * baseSpacing.patterns.containerPadding,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: baseBorderRadius.sm,  // 6px for buttons (Atlas)
            textTransform: 'none',
            fontWeight: 600,
            '&:focus-visible': {
              outline: `${baseFocus.ring.width}px ${baseFocus.ring.style}`,
              outlineOffset: `${baseFocus.ring.offset}px`,
            },
          },
        },
      },
      // Atlas gold action button variant
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: baseBorderRadius.sm,  // 6px for chips (Atlas)
          },
          label: {
            fontSize: baseTypography.fontSize.xs,
            fontStyle: 'normal',
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontSize: baseTypography.fontSize.xs,
            fontStyle: 'normal',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: baseBorderRadius.lg,  // 12px for dialogs (Atlas)
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          rounded: {
            borderRadius: baseBorderRadius.lg,  // 12px for paper (Atlas)
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: baseBorderRadius.md,  // 8px for inputs (Atlas)
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.text.secondary,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.text.primary,
            },
          }),
          input: ({ theme }) => ({
            '&::placeholder': {
              color: theme.palette.text.secondary,
              opacity: 1,
            },
          }),
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            fontSize: baseTypography.fontSize.sm,
          },
          input: ({ theme }) => ({
            '&::placeholder': {
              color: theme.palette.text.secondary,
              opacity: 1,
            },
          }),
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: { fontSize: baseTypography.fontSize.sm },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          body: { fontSize: baseTypography.fontSize.sm },
          head: { fontSize: baseTypography.fontSize.xs, fontWeight: 600 },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontSize: baseTypography.variants.subtitle1.fontSize,
            fontWeight: 700,
            padding: `${baseSpacing.unit * contentSpacing.headerY}px ${baseSpacing.unit * contentSpacing.gutter}px`,
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: { padding: baseSpacing.unit * contentSpacing.gutter },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: `${baseSpacing.unit * contentSpacing.headerY}px ${baseSpacing.unit * contentSpacing.gutter}px`,
            flexWrap: 'wrap',
            gap: baseSpacing.unit * contentSpacing.textGap,
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            borderRadius: baseBorderRadius.lg,  // 12px for accordion (Atlas)
            '&:before': {
              display: 'none',
            },
          },
        },
      },
    },
  };

  // Select the appropriate palette based on mode
  const clientPalette = mode === 'dark' && clientOverrides.palette.dark
    ? clientOverrides.palette.dark
    : clientOverrides.palette.light;

  // Merge base theme with client overrides (without custom props)
  const mergedOptions: ThemeOptions = deepmerge(baseTheme, {
    palette: clientPalette,
    typography: clientOverrides.typography || {},
    components: clientOverrides.components || {},
  });

  // Create theme first
  const theme = createTheme(mergedOptions);

  // Add custom properties after creation (createTheme strips unknown props)
  (theme as any).logo = clientOverrides.logo;
  (theme as any).clientName = clientOverrides.clientName;
  theme.portalTitle = clientOverrides.portalTitle;
  theme.dashboardBannerImage = clientOverrides.dashboardBannerImage;
  theme.hideHeaderClientName = clientOverrides.hideHeaderClientName;
  theme.headerLogo = clientOverrides.headerLogo;
  (theme as any).dataGrid = clientOverrides.dataGrid;

  return theme;
}
